import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI, reviewsAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-primary-100 text-primary-700',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const OrdersPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('buying');
  const [statusFilter, setStatusFilter] = useState('');
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, review_text: '' });

  const isProvider = user?.role === 'provider';

  useEffect(() => {
    setLoading(true);
    const params = statusFilter ? { status: statusFilter } : {};
    const fetcher = view === 'selling' ? ordersAPI.getSeller(params) : ordersAPI.getMy(params);
    fetcher
      .then((res) => setOrders(res.data.orders || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [view, statusFilter]);

  const updateStatus = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, { status });
      setOrders(orders.map((o) => o.id === orderId ? { ...o, status } : o));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-RW', { style: 'currency', currency: 'RWF', maximumFractionDigits: 0 }).format(price);

  const handleReview = async (e) => {
    e.preventDefault();
    try {
      await reviewsAPI.create({ product_id: reviewModal.product_id, ...reviewForm });
      setReviewModal(null);
      setReviewForm({ rating: 5, review_text: '' });
      alert('Review submitted!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit review');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50/50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">My Orders</h1>

        {/* View toggle for providers */}
        {isProvider && (
          <div className="flex gap-2 mb-4">
            <button onClick={() => setView('buying')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'buying' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>
              My Purchases
            </button>
            <button onClick={() => setView('selling')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'selling' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>
              Received Orders
            </button>
          </div>
        )}

        {/* Status filter */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['', 'pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${statusFilter === s ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border hover:bg-gray-50'}`}>
              {s || 'All'}
            </button>
          ))}
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-4">📦</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No orders yet</h2>
            <p className="text-gray-500 mb-6">{view === 'selling' ? 'No orders received yet.' : 'Browse products and place your first order!'}</p>
            <Link to="/products" className="bg-primary-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-primary-700 transition">
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{order.product_name || `Order #${order.id}`}</h3>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>Quantity: <span className="text-gray-700 font-medium">{order.quantity}</span></p>
                      <p>Total: <span className="text-gray-900 font-semibold">{formatPrice(order.total_price)}</span></p>
                      {view === 'selling' && order.buyer_name && (
                        <p>Buyer: <span className="text-gray-700">{order.buyer_name}</span></p>
                      )}
                      {view === 'buying' && order.seller_name && (
                        <p>Seller: <span className="text-gray-700">{order.seller_name}</span></p>
                      )}
                      <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString('en-RW', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>

                  {/* Seller actions */}
                  {view === 'selling' && order.status !== 'delivered' && order.status !== 'cancelled' && (
                    <div className="flex flex-wrap gap-2">
                      {order.status === 'pending' && (
                        <>
                          <button onClick={() => updateStatus(order.id, 'confirmed')}
                            className="px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition">
                            Confirm
                          </button>
                          <button onClick={() => updateStatus(order.id, 'cancelled')}
                            className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition">
                            Cancel
                          </button>
                        </>
                      )}
                      {order.status === 'confirmed' && (
                        <button onClick={() => updateStatus(order.id, 'shipped')}
                          className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition">
                          Mark Shipped
                        </button>
                      )}
                      {order.status === 'shipped' && (
                        <button onClick={() => updateStatus(order.id, 'delivered')}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition">
                          Mark Delivered
                        </button>
                      )}
                    </div>
                  )}

                  {/* Buyer cancel */}
                  {view === 'buying' && order.status === 'pending' && (
                    <button onClick={() => updateStatus(order.id, 'cancelled')}
                      className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition">
                      Cancel Order
                    </button>
                  )}
                  {view === 'buying' && order.status === 'delivered' && (
                    <button onClick={() => setReviewModal(order)}
                      className="px-3 py-1.5 bg-yellow-500 text-white text-xs font-medium rounded-lg hover:bg-yellow-600 transition">
                      ⭐ Review Product
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Review Product</h3>
            <p className="text-sm text-gray-500 mb-4">{reviewModal.product_name}</p>
            <form onSubmit={handleReview} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Rating</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <svg key={star} onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      className={`w-8 h-8 cursor-pointer ${star <= reviewForm.rating ? 'text-yellow-400' : 'text-gray-200'} hover:text-yellow-300`}
                      fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Your Review</label>
                <textarea value={reviewForm.review_text} onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                  rows={3} placeholder="How was this product?" className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 outline-none" />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-primary-600 text-white py-2.5 rounded-xl font-semibold hover:bg-primary-700 transition text-sm">Submit</button>
                <button type="button" onClick={() => { setReviewModal(null); setReviewForm({ rating: 5, review_text: '' }); }}
                  className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
